import { useEffect } from 'react';

interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  language?: 'en' | 'de';
}

export const useSEO = (data: SEOData) => {
  useEffect(() => {
    // Update document title
    document.title = data.title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updatePropertyTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Basic meta tags
    updateMetaTag('description', data.description);
    if (data.keywords) {
      updateMetaTag('keywords', data.keywords);
    }

    // Open Graph tags (for when links are shared)
    updatePropertyTag('og:title', data.title);
    updatePropertyTag('og:description', data.description);
    updatePropertyTag('og:type', data.type || 'website');
    if (data.url) {
      updatePropertyTag('og:url', data.url);
    }
    if (data.image) {
      updatePropertyTag('og:image', data.image);
    }
    updatePropertyTag('og:locale', data.language === 'de' ? 'de_DE' : 'en_US');

    // Twitter Card tags (for when links are shared on Twitter)
    updatePropertyTag('twitter:card', 'summary_large_image');
    updatePropertyTag('twitter:title', data.title);
    updatePropertyTag('twitter:description', data.description);
    if (data.image) {
      updatePropertyTag('twitter:image', data.image);
    }

    // Language and other meta tags
    updateMetaTag('language', data.language || 'en');
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('author', 'Run for M.E. Team');

    // Update html lang attribute
    const htmlElement = document.documentElement;
    htmlElement.lang = data.language || 'en';

    // Cleanup function
    return () => {
      // Reset to default values when component unmounts
      document.title = 'Run for M.E. - Marathon Fundraiser';
      updateMetaTag('description', 'Marathon fundraiser for M.E. (Myalgic Encephalomyelitis) - Running for a cause that matters');
      updateMetaTag('language', 'en');
      htmlElement.lang = 'en';
    };
  }, [data.title, data.description, data.keywords, data.image, data.url, data.type, data.language]);
};
